import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { QRCodeSVG } from 'qrcode.react';
import { useWallet } from './WalletContext';

const UserProfilePage = ({ language, user, refreshUser }) => {
  const navigate = useNavigate();
  const [profileData, setProfileData] = useState({
    name: '',
    phone: '',
    address: '',
    city: '',
    state: '',
    pincode: ''
  });
  const [passwordData, setPasswordData] = useState({
    current_password: '',
    new_password: '',
    confirm_password: ''
  });
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState({ type: '', text: '' });
  const [activeTab, setActiveTab] = useState('profile'); // profile | password | wallet
  
  const { balance, isPremium, recharge, withdraw, loading: walletLoading } = useWallet();
  const [walletAmount, setWalletAmount] = useState('');
  const [withdrawReason, setWithdrawReason] = useState('');

  const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
  const API = BACKEND_URL.endsWith('/api') ? BACKEND_URL : `${BACKEND_URL}/api`;

  useEffect(() => {
    if (!user) {
      navigate('/');
      return;
    }
    
    // Initialize profile data from user
    setProfileData({
      name: user.name || '',
      phone: user.phone || '',
      address: user.address || '',
      city: user.city || '',
      state: user.state || '',
      pincode: user.pincode || ''
    });
  }, [user, navigate]);

  const handleProfileUpdate = async (e) => {
    e.preventDefault();
    setLoading(true);
    setMessage({ type: '', text: '' });

    try {
      const token = localStorage.getItem('token');
      const response = await axios.put(`${API}/auth/profile`, profileData, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      setMessage({ 
        type: 'success', 
        text: language === 'en' ? 'Profile updated successfully!' : 'சுயவிவரம் வெற்றிகரமாக புதுப்பிக்கப்பட்டது!' 
      });
      
      // Refresh user data
      if (refreshUser) {
        await refreshUser();
      }
    } catch (error) {
      setMessage({ 
        type: 'error', 
        text: error.response?.data?.detail || (language === 'en' ? 'Failed to update profile' : 'சுயவிவரம் புதுப்பிக்க முடியவில்லை') 
      });
    } finally {
      setLoading(false);
    }
  };

  const handlePasswordChange = async (e) => {
    e.preventDefault();
    setLoading(true);
    setMessage({ type: '', text: '' });

    // Validate passwords match
    if (passwordData.new_password !== passwordData.confirm_password) {
      setMessage({ 
        type: 'error', 
        text: language === 'en' ? 'New passwords do not match!' : 'புதிய கடவுச்சொற்கள் பொருந்தவில்லை!' 
      });
      setLoading(false);
      return;
    }

    // Validate password length
    if (passwordData.new_password.length < 6) {
      setMessage({ 
        type: 'error', 
        text: language === 'en' ? 'Password must be at least 6 characters!' : 'கடவுச்சொல் குறைந்தது 6 எழுத்துக்களாக இருக்க வேண்டும்!' 
      });
      setLoading(false);
      return;
    }

    try {
      const token = localStorage.getItem('token');
      console.log('🔐 Changing password, token exists:', !!token);
      
      const response = await axios.post(`${API}/auth/change-password`, {
        current_password: passwordData.current_password,
        new_password: passwordData.new_password
      }, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      console.log('✅ Password change response:', response.data);
      
      setMessage({ 
        type: 'success', 
        text: language === 'en' ? 'Password changed successfully!' : 'கடவுச்சொல் வெற்றிகரமாக மாற்றப்பட்டது!' 
      });
      
      // Clear password fields
      setPasswordData({
        current_password: '',
        new_password: '',
        confirm_password: ''
      });
    } catch (error) {
      console.error('❌ Password change error:', error);
      console.error('Error response:', error.response?.data);
      
      const errorMessage = error.response?.data?.detail || 
                          error.response?.data?.message ||
                          (language === 'en' ? 'Failed to change password. Please check your current password.' : 'கடவுச்சொல் மாற்ற முடியவில்லை. உங்கள் தற்போதைய கடவுச்சொல்லைச் சரிபார்க்கவும்.');
      
      setMessage({ 
        type: 'error', 
        text: errorMessage
      });
    } finally {
      setLoading(false);
    }
  };

  if (!user) return null;

  return (
    <div className="min-h-screen bg-gradient-to-br from-green-50 to-orange-50 py-12 px-4">
      <div className="max-w-4xl mx-auto">
        <h1 className="text-4xl font-bold text-green-800 mb-8">
          {language === 'en' ? 'My Profile' : 'எனது சுயவிவரம்'}
        </h1>

        {/* Tabs */}
        <div className="bg-white rounded-t-lg shadow-md">
          <div className="flex border-b">
            <button
              onClick={() => setActiveTab('profile')}
              className={`flex-1 py-4 px-6 font-semibold transition ${
                activeTab === 'profile'
                  ? 'text-green-600 border-b-2 border-green-600 bg-green-50'
                  : 'text-gray-600 hover:bg-gray-50'
              }`}
            >
              {language === 'en' ? '👤 Profile Details' : '👤 சுயவிவர விவரங்கள்'}
            </button>
            <button
              onClick={() => setActiveTab('password')}
              className={`flex-1 py-4 px-6 font-semibold transition ${
                activeTab === 'password'
                  ? 'text-green-600 border-b-2 border-green-600 bg-green-50'
                  : 'text-gray-600 hover:bg-gray-50'
              }`}
            >
              {language === 'en' ? '🔒 Change Password' : '🔒 கடவுச்சொல் மாற்று'}
            </button>
          </div>
        </div>

        <div className="bg-white rounded-b-lg shadow-md p-8">
          {/* Message Display */}
          {message.text && (
            <div className={`mb-6 p-4 rounded-lg ${
              message.type === 'success' 
                ? 'bg-green-100 text-green-800 border border-green-200' 
                : 'bg-red-100 text-red-800 border border-red-200'
            }`}>
              {message.text}
            </div>
          )}

          {/* Profile Tab */}
          {activeTab === 'profile' && (
            <form onSubmit={handleProfileUpdate} className="space-y-6">
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
                <p className="text-sm text-blue-800">
                  <strong>{language === 'en' ? 'Email:' : 'மின்னஞ்சல்:'}</strong> {user.email}
                </p>
                <p className="text-xs text-blue-600 mt-1">
                  {language === 'en' ? '(Email cannot be changed - it is your account ID)' : '(மின்னஞ்சலை மாற்ற முடியாது - இது உங்கள் கணக்கு அடையாளம்)'}
                </p>
              </div>

              <div className="grid md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    {language === 'en' ? 'Full Name *' : 'முழு பெயர் *'}
                  </label>
                  <input
                    type="text"
                    value={profileData.name}
                    onChange={(e) => setProfileData({...profileData, name: e.target.value})}
                    className="w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-green-500"
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    {language === 'en' ? 'Phone Number' : 'தொலைபேசி எண்'}
                  </label>
                  <input
                    type="tel"
                    value={profileData.phone}
                    onChange={(e) => setProfileData({...profileData, phone: e.target.value})}
                    className="w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-green-500"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  {language === 'en' ? 'Address' : 'முகவரி'}
                </label>
                <textarea
                  value={profileData.address}
                  onChange={(e) => setProfileData({...profileData, address: e.target.value})}
                  className="w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-green-500"
                  rows="3"
                />
              </div>

              <div className="grid md:grid-cols-3 gap-6">
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    {language === 'en' ? 'City' : 'நகரம்'}
                  </label>
                  <input
                    type="text"
                    value={profileData.city}
                    onChange={(e) => setProfileData({...profileData, city: e.target.value})}
                    className="w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-green-500"
                  />
                </div>

                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    {language === 'en' ? 'State' : 'மாநிலம்'}
                  </label>
                  <input
                    type="text"
                    value={profileData.state}
                    onChange={(e) => setProfileData({...profileData, state: e.target.value})}
                    className="w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-green-500"
                  />
                </div>

                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    {language === 'en' ? 'Pincode' : 'பின் குறியீடு'}
                  </label>
                  <input
                    type="text"
                    value={profileData.pincode}
                    onChange={(e) => setProfileData({...profileData, pincode: e.target.value})}
                    className="w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-green-500"
                  />
                </div>
              </div>

              <button
                type="submit"
                disabled={loading}
                className="w-full bg-green-600 text-white py-4 rounded-lg hover:bg-green-700 font-bold text-lg disabled:opacity-50 transition"
              >
                {loading ? (language === 'en' ? 'Updating...' : 'புதுப்பிக்கிறது...') : (language === 'en' ? 'Update Profile' : 'சுயவிவரத்தை புதுப்பிக்கவும்')}
              </button>
            </form>
          )}

          {/* Password Tab */}
          {activeTab === 'password' && (
            <form onSubmit={handlePasswordChange} className="space-y-6">
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  {language === 'en' ? 'Current Password *' : 'தற்போதைய கடவுச்சொல் *'}
                </label>
                <input
                  type="password"
                  value={passwordData.current_password}
                  onChange={(e) => setPasswordData({...passwordData, current_password: e.target.value})}
                  className="w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-green-500"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  {language === 'en' ? 'New Password * (min 6 characters)' : 'புதிய கடவுச்சொல் * (குறைந்தது 6 எழுத்துக்கள்)'}
                </label>
                <input
                  type="password"
                  value={passwordData.new_password}
                  onChange={(e) => setPasswordData({...passwordData, new_password: e.target.value})}
                  className="w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-green-500"
                  required
                  minLength="6"
                />
              </div>

              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  {language === 'en' ? 'Confirm New Password *' : 'புதிய கடவுச்சொல்லை உறுதிப்படுத்தவும் *'}
                </label>
                <input
                  type="password"
                  value={passwordData.confirm_password}
                  onChange={(e) => setPasswordData({...passwordData, confirm_password: e.target.value})}
                  className="w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-green-500"
                  required
                  minLength="6"
                />
              </div>

              <button
                type="submit"
                disabled={loading}
                className="w-full bg-orange-600 text-white py-4 rounded-lg hover:bg-orange-700 font-bold text-lg disabled:opacity-50 transition"
              >
                {loading ? (language === 'en' ? 'Changing...' : 'மாற்றுகிறது...') : (language === 'en' ? 'Change Password' : 'கடவுச்சொல்லை மாற்று')}
              </button>
            </form>
          )}
        </div>
      </div>
    </div>
  );
};

export default UserProfilePage;
